import { useEffect, useRef } from "react";
import {
  Search,
  Eraser,
  Download,
  Loader2,
  RotateCw,
  Wifi,
  WifiOff,
  Radio,
} from "lucide-react";
import { Button, cn, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, ToggleGroup, ToggleGroupItem } from "@fox1ck-org/ui";
import { TIME_RANGE_OPTIONS, type LogsMode } from "./types";
import { getLevelStyle } from "./levelStyles";
import type { StreamStatus } from "./useLogsStream";

interface LogsToolbarProps {
  mode: LogsMode;
  onModeChange: (mode: LogsMode) => void;
  pod: string;
  onPodChange: (pod: string) => void;
  podOptions: Array<{ name: string; label?: string }>;
  container: string;
  onContainerChange: (container: string) => void;
  containerOptions: string[];
  levels: string[];
  onLevelsChange: (levels: string[]) => void;
  availableLevels: string[];
  search: string;
  onSearchChange: (q: string) => void;
  range: string;
  onRangeChange: (r: string) => void;
  onClear: () => void;
  onDownload: () => void;
  onRefresh?: () => void;
  isFetching?: boolean;
  streamStatus: StreamStatus;
  reconnectAttempt: number;
  searchInputRef?: React.RefObject<HTMLInputElement>;
}

const DEFAULT_LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"];

export function LogsToolbar(props: LogsToolbarProps) {
  const {
    mode,
    onModeChange,
    pod,
    onPodChange,
    podOptions,
    container,
    onContainerChange,
    containerOptions,
    levels,
    onLevelsChange,
    availableLevels,
    search,
    onSearchChange,
    range,
    onRangeChange,
    onClear,
    onDownload,
    onRefresh,
    isFetching,
    streamStatus,
    reconnectAttempt,
    searchInputRef,
  } = props;

  const localRef = useRef<HTMLInputElement>(null);
  const inputRef = (searchInputRef ?? localRef) as React.RefObject<HTMLInputElement>;

  // Slash-key focuses search globally.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      if (e.key === "/") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [inputRef]);

  const toggleLevel = (level: string) => {
    if (levels.includes(level)) {
      onLevelsChange(levels.filter((l) => l !== level));
    } else {
      onLevelsChange([...levels, level]);
    }
  };

  const allLevels = availableLevels.length > 0 ? availableLevels : DEFAULT_LEVELS;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2">
      {/* Mode toggle */}
      <ToggleGroup
        type="single"
        size="sm"
        value={mode}
        onValueChange={(v) => v && onModeChange(v as LogsMode)}
        className="shrink-0"
      >
        <ToggleGroupItem value="live" className="h-7 px-2 text-[11px] gap-1">
          <Radio
            className={cn(
              "h-3 w-3",
              mode === "live" && streamStatus === "open" && "text-green-400 animate-pulse"
            )}
          />
          Онлайн
        </ToggleGroupItem>
        <ToggleGroupItem value="history" className="h-7 px-2 text-[11px]">
          История
        </ToggleGroupItem>
      </ToggleGroup>

      {/* Pod selector */}
      <Select value={pod || "_all"} onValueChange={(v) => onPodChange(v === "_all" ? "" : v)}>
        <SelectTrigger className="w-[180px] h-7 text-xs">
          <SelectValue placeholder={mode === "live" ? "Выбрать под автоматически" : "Все поды"} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">{mode === "live" ? "Авто (первый под)" : "Все поды"}</SelectItem>
          {podOptions.map((p) => (
            <SelectItem key={p.name} value={String(p.name)}>
              {p.label ?? p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Container selector (live mode only — historical search filters by pod) */}
      {mode === "live" && containerOptions.length > 1 && (
        <Select
          value={container || "_all"}
          onValueChange={(v) => onContainerChange(v === "_all" ? "" : v)}
        >
          <SelectTrigger className="w-[140px] h-7 text-xs">
            <SelectValue placeholder="Все контейнеры" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Все контейнеры</SelectItem>
            {containerOptions.map((c) => (
              <SelectItem key={c} value={String(c)}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Level filter */}
      <div className="flex items-center gap-1">
        {allLevels.map((level) => {
          const style = getLevelStyle(level);
          const active = levels.length === 0 || levels.includes(level);
          return (
            <button
              key={level}
              type="button"
              onClick={() => toggleLevel(level)}
              className={cn(
                "px-1.5 h-6 rounded text-[10px] font-semibold border uppercase transition-colors",
                active
                  ? style.pill
                  : "border-border text-muted-foreground/50 hover:text-foreground hover:bg-accent"
              )}
            >
              {level}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex-1 min-w-[180px] relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              onSearchChange("");
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Фильтр сообщений… нажмите /"
          className="pl-8 h-7 text-xs"
        />
      </div>

      {/* Time range — historical only */}
      {mode === "history" && (
        <ToggleGroup
          type="single"
          size="sm"
          value={range}
          onValueChange={(v) => v && onRangeChange(v)}
        >
          {TIME_RANGE_OPTIONS.map((r) => (
            <ToggleGroupItem key={r.value} value={r.value} className="h-7 px-2 text-[11px]">
              {r.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}

      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-[11px] gap-1"
        onClick={onClear}
        title="Очистить буфер"
      >
        <Eraser className="h-3 w-3" />
        Очистить
      </Button>

      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-[11px] gap-1"
        onClick={onDownload}
        title="Скачать видимые журналы"
      >
        <Download className="h-3 w-3" />
      </Button>

      {mode === "history" && onRefresh && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onRefresh}
          title="Обновить"
        >
          <RotateCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
        </Button>
      )}

      {/* Stream status indicator (live only) */}
      {mode === "live" && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground border-l pl-2 ml-1">
          {streamStatus === "open" ? (
            <>
              <Wifi className="h-3 w-3 text-green-400" />
              <span>Онлайн</span>
            </>
          ) : streamStatus === "connecting" ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>Подключаемся…</span>
            </>
          ) : streamStatus === "reconnecting" ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin text-yellow-400" />
              <span>Переподключение #{reconnectAttempt}</span>
            </>
          ) : streamStatus === "missing-pod" ? (
            <>
              <WifiOff className="h-3 w-3 text-yellow-400" />
              <span>Нет пода</span>
            </>
          ) : (
            <>
              <WifiOff className="h-3 w-3 text-muted-foreground/60" />
              <span>Ожидание</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
